'use client'

import { useCallback, useEffect, useState } from 'react'
import { useRouter, useSearchParams } from 'next/navigation'

import { useRovingTabs } from '@/hooks/use-roving-tabs'
import { FalToolStudio } from '@/components/marketing/tools/fal-tool-studio'
import {
  MediaWorkflowIdleSamples,
  type MediaWorkflowIdleSample,
} from '@/components/marketing/tools/media-workflow-idle-samples'
import { prefetchFalToolSpec } from '@/hooks/use-fal-tool'
import { track } from '@/lib/analytics/track'
import { cn } from '@/lib/utils'

export type MediaWorkflowMode = {
  /** Tab label. Only rendered when the page has more than one mode. */
  label: string
  /** Must match a registered slug in apps/api/routers/landing/fal_tools.py. */
  slug: string
  /** Used in the auth dialog copy: "Create your account to use the {title}". */
  title: string
  /**
   * One line above the form. Optional because a single-mode page has already
   * said this in its hero intro, and repeating it two elements later is the
   * text density these pages were criticised for.
   */
  description?: string
  promptPlaceholder?: string
  /**
   * Overrides for the studio's own control headings, for tools whose prompt is
   * composed server-side. On the real-estate tool the listing URL is the real
   * input and the text box is optional extra direction — calling it "Prompt"
   * tells a visitor they have to write one. Omit both and the studio keeps its
   * defaults.
   */
  promptLabel?: string
  imageLabel?: string
  samplePrompts?: string[]
  /** Shown in the result column until this visitor has generated something. */
  idleSamples?: MediaWorkflowIdleSample[]
  /** One line above them, naming what they are. */
  idleSamplesNote?: string
}

export function MediaWorkflowStudio({
  modes,
  pagePath,
}: {
  modes: MediaWorkflowMode[]
  pagePath: string
}) {
  const router = useRouter()
  const searchParams = useSearchParams()

  const mediaValue = useCallback((slug: string) => {
    if (slug.startsWith('video-')) return 'video'
    if (slug.startsWith('image-')) return 'image'
    return slug
  }, [])

  const slugFromUrl = useCallback(() => {
    const requested = searchParams.get('media')
    return (
      modes.find(mode => mediaValue(mode.slug) === requested)?.slug ??
      modes[0]?.slug ??
      ''
    )
  }, [mediaValue, modes, searchParams])

  const [activeSlug, setActiveSlug] = useState(slugFromUrl)

  // The URL is what says which mode is showing, and it changes without going
  // through selectMode: Back and Forward move between this page's own history
  // entries, one per mode switch. Seeding state from the URL only on first
  // render leaves the panel showing the previous tool while the address bar
  // names a different one.
  useEffect(() => {
    setActiveSlug(slugFromUrl())
  }, [slugFromUrl])

  const selectMode = useCallback(
    (slug: string, { replace = false }: { replace?: boolean } = {}) => {
      if (slug === activeSlug) return
      setActiveSlug(slug)
      const params = new URLSearchParams(searchParams.toString())
      params.set('media', mediaValue(slug))
      const href = `${pagePath}?${params.toString()}`
      // Arrow keys scrub through the tabs, and one history entry per keypress
      // would bury the ad that sent the visitor here under a stack of its own
      // tab changes — on the page where Back is the escape hatch.
      if (replace) router.replace(href, { scroll: false })
      else router.push(href, { scroll: false })
      // Which half of an image-or-video page the paid click actually wanted.
      // Without it the two engines are indistinguishable in the funnel and the
      // ad copy cannot be tuned to either.
      track('media_workflow_mode_switched', { page: pagePath, mode: slug })
    },
    [activeSlug, mediaValue, pagePath, router, searchParams],
  )

  const activeIndex = Math.max(
    0,
    modes.findIndex(mode => mode.slug === activeSlug),
  )
  const active = modes[activeIndex] ?? modes[0]

  // The keyboard contract lives in the shared hook (`hooks/use-roving-tabs.ts`),
  // which was extracted from this component when a second row-of-tabs widget
  // needed it. `viaKeyboard` is why the hook reports it: arrowing across the
  // row replaces the history entry, picking one pushes — a keyboard user must
  // not bury the ad that sent them here under a stack of their own tab changes.
  const tabs = useRovingTabs({
    count: modes.length,
    selectedIndex: activeIndex,
    enabled: modes.length > 1,
    onSelect: (index, { viaKeyboard }) => {
      const target = modes[index]
      if (target) selectMode(target.slug, { replace: viaKeyboard })
    },
  })

  if (!active) return null

  return (
    <div>
      {modes.length > 1 ? (
        <div
          {...tabs.tablistProps}
          className="mx-auto mb-5 grid max-w-xl grid-cols-2 rounded-2xl border border-white/10 bg-white/5 p-1.5 shadow-2xl backdrop-blur"
          aria-label="Media type"
        >
          {modes.map((mode, index) => {
            const selected = mode.slug === active.slug
            return (
              <button
                key={mode.slug}
                {...tabs.tabProps(index)}
                className={cn(
                  'rounded-xl px-4 py-3 text-sm font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-orange-500 focus-visible:outline-none',
                  selected
                    ? 'bg-orange-500 text-stone-950 shadow-sm'
                    : 'text-white/50 hover:bg-white/5 hover:text-white',
                )}
              >
                {mode.label}
              </button>
            )
          })}
        </div>
      ) : null}

      <div {...tabs.panelProps}>
        {active.description ? (
          <p className="mx-auto mb-5 max-w-2xl text-center text-sm leading-6 text-white/55">
            {active.description}
          </p>
        ) : null}
        <FalToolStudio
          key={active.slug}
          slug={active.slug}
          pagePath={pagePath}
          title={active.title}
          showEngine={false}
          promptPlaceholder={active.promptPlaceholder}
          promptLabel={active.promptLabel}
          imageLabel={active.imageLabel}
          samplePrompts={active.samplePrompts}
          idleSamples={
            active.idleSamples?.length ? (
              <MediaWorkflowIdleSamples
                samples={active.idleSamples}
                note={active.idleSamplesNote ?? 'Real runs from this tool.'}
              />
            ) : undefined
          }
        />
      </div>
    </div>
  )
}
